/**
 * Range overlap validation utility.
 *
 * Rules:
 * - from must be a positive number
 * - to must be greater than from
 * - No two ranges may share or overlap a boundary (i.e. [a,b] and [c,d] conflict when a <= d && c <= b)
 */

export interface RangeItem {
    from: string
    to: string
}

export interface RangeError {
    /** index of the range with the problem */
    index: number
    message: string
}

export function validateRanges(ranges: RangeItem[]): RangeError[] {
    const errors: RangeError[] = []

    // Parse to numbers; treat blank as NaN
    const parsed = ranges.map(r => ({
        from: r.from.trim() === '' ? NaN : parseFloat(r.from),
        to: r.to.trim() === '' ? NaN : parseFloat(r.to),
    }))

    parsed.forEach((range, i) => {
        // Skip entirely empty rows
        if (isNaN(range.from) && isNaN(range.to)) return

        if (isNaN(range.from) || range.from < 0) {
            errors.push({
                index: i,
                message: '"From" must be a valid non-negative number.',
            })
            return
        }
        if (isNaN(range.to)) {
            errors.push({ index: i, message: '"To" must be a valid number.' })
            return
        }
        if (range.to <= range.from) {
            errors.push({
                index: i,
                message: '"To" must be greater than "From".',
            })
            return
        }

        // Check against every other fully-specified range
        parsed.forEach((other, j) => {
            if (i === j) return
            if (isNaN(other.from) || isNaN(other.to) || other.to <= other.from)
                return

            // Overlap condition: ranges [a,b] and [c,d] overlap (including shared boundaries) when a <= d && c <= b
            const overlaps = range.from <= other.to && other.from <= range.to

            if (overlaps) {
                // Only report the error once per pair (i < j)
                if (i < j) {
                    const overlapFrom = Math.max(range.from, other.from)
                    const overlapTo = Math.min(range.to, other.to)
                    errors.push({
                        index: i,
                        message: `Range ${range.from}–${range.to} overlaps with range ${other.from}–${other.to} (conflict between ${overlapFrom} and ${overlapTo}). Each amount can only belong to one range.`,
                    })
                }
            }
        })
    })

    return errors
}

/** Returns a map of index → error message for quick lookup in the UI */
export function rangeErrorMap(errors: RangeError[]): Record<number, string> {
    const map: Record<number, string> = {}
    errors.forEach(e => {
        // Keep the first error per index
        if (!(e.index in map)) map[e.index] = e.message
    })
    return map
}
